import { useEffect, useState } from "react";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { CheckCircle, X, PaperPlaneTilt } from "@phosphor-icons/react";

const hay = (s) => [s.company_name, s.contact_name, s.name, s.email, s.phone, s.trade, s.postcode, s.town_city, s.county, s.coverage_area,
  Array.isArray(s.sectors) ? s.sectors.join(" ") : s.sectors, Array.isArray(s.areas_covered) ? s.areas_covered.join(" ") : s.areas_covered].filter(Boolean).join(" ").toLowerCase();

/* "Invite Supplier to Quote" — pick from the directory and/or type emails; each gets a brief PDF + submit link. */
export default function InviteSuppliersDialog({ open, onOpenChange, onSend, briefName = "Job Brief PDF", testPrefix = "" }) {
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [emailsInput, setEmailsInput] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) { setSearch(""); setSelected([]); setEmailsInput(""); return; }
    API.get("/suppliers").then(({ data }) => setSuppliers((Array.isArray(data) ? data : data.vendors || []).filter(v => v.email))).catch(() => {});
  }, [open]);

  const toggle = (s) => setSelected(prev => prev.find(x => x.id === s.id) ? prev.filter(x => x.id !== s.id) : [...prev, s]);
  const q = search.trim().toLowerCase();
  const list = [...suppliers].sort((a, b) => (a.company_name || a.contact_name || "").localeCompare(b.company_name || b.contact_name || "", undefined, { sensitivity: "base" }))
    .filter(s => !q || q.split(/\s+/).filter(Boolean).every(w => hay(s).includes(w)));

  const send = async () => {
    const manual = emailsInput.split(/[,;\n]/).map(e => e.trim()).filter(Boolean);
    const emails = [...new Set([...selected.map(s => s.email).filter(Boolean), ...manual])];
    if (!emails.length) return;
    setSending(true);
    try { await onSend(emails); onOpenChange(false); } finally { setSending(false); }
  };
  const count = selected.length + (emailsInput.trim() ? 1 : 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="request-quotes-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">Invite Supplier to Quote</DialogTitle>
          <DialogDescription>Select suppliers from your directory or enter email addresses manually. Each supplier will receive a professional email with a <strong>{briefName}</strong> attached and a link to submit their quote online.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-semibold">Choose from Suppliers</Label>
            <Input placeholder="Search by company, email, or trade..." value={search} onChange={e => setSearch(e.target.value)} className="mt-1.5 text-xs" data-testid="supplier-search-input" />
            <div className="mt-2 max-h-44 overflow-y-auto border border-border rounded-lg divide-y divide-border">
              {list.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4">No suppliers found</p> : list.slice(0, 30).map(s => {
                const on = selected.some(x => x.id === s.id);
                return (
                  <button key={s.id} type="button" onClick={() => toggle(s)} className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${on ? "bg-primary/5" : "hover:bg-muted/50"}`} data-testid={`supplier-pick-${s.id}`}>
                    <div className={`h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${on ? "border-primary bg-primary" : "border-muted-foreground/30"}`}>{on && <CheckCircle size={12} className="text-primary-foreground" weight="bold" />}</div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium truncate">{s.company_name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{s.email} {s.trade ? `• ${s.trade}` : ""}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            {selected.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {selected.map(s => <Badge key={s.id} variant="secondary" className="text-[10px] gap-1 pr-1">{s.company_name}<button type="button" onClick={() => toggle(s)} className="ml-0.5 hover:text-destructive"><X size={10} /></button></Badge>)}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs font-semibold">Or Enter Email Addresses</Label>
            <Textarea value={emailsInput} onChange={e => setEmailsInput(e.target.value)} rows={3} placeholder={"supplier1@email.com\nsupplier2@email.com"} className="mt-1.5 font-mono text-xs" data-testid="vendor-emails-input" />
            <p className="text-[10px] text-muted-foreground mt-1">One email per line, or separate with commas. For suppliers not yet in your directory.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={send} disabled={sending || count === 0} data-testid="send-quote-requests-btn">
            <PaperPlaneTilt size={14} className="mr-1" /> {sending ? "Sending..." : `Send Invite${count > 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
